// planProposal.js — E1-WS2-T01 (refactor-deepening / F1, Plan/DAG Orchestration seam).
//
// Deep module owning the PLAN-PROPOSAL flow that ran inline in runAgent's finally: after
// a plan-mode (or spurious present_plan) turn settles, surface the plan with Approve/
// Reject. It PREFERS the structured plan the model emitted via present_plan
// (agentResult.plan) and FALLS BACK to parsing the agent's prose "## Plan". It sets the
// pending plan (drives the PlanProposalCard), dispatches the propose (drives the
// TodoDropdown), and projects the todos onto the right-rail Tasks board.
//
// Deletion test (01-DOCTRINE): delete this module and the structured/prose parsing +
// proposal building + the three side-effects (setPendingPlan / dispatchPlan propose /
// board projection) scatter back into the finally. DEEP.
//
// Behavior byte-identical: same branches, same order, same helpers as the inline block —
// notably `setPendingPlan` fires for a structured plan and for a prose plan INDEPENDENTLY
// of whether a `proposal` (from parsePlanTodos) was built, exactly as before. PURE
// helpers imported; the store/UI effects are injected.

import { ensurePlanQuestions } from '../../../lib/planContinuation.js';
import { parsePlanTodos, planFromProse } from '../../../lib/loop/parsePlanTodos.js';
import { planTodosToTasks } from '../../../lib/tasks/taskGraph.js';

// The STRUCTURED plan (present_plan terminal tool): store the full payload as the pending
// plan, then project its steps onto {title, todos}. Returns the proposal, or null when no
// step has an action (setPendingPlan still happened — the card renders from the payload).
function structuredProposal(structured, sessionId, setPendingPlan) {
  setPendingPlan(sessionId, ensurePlanQuestions(structured));
  const title = String(structured.summary || '').slice(0, 120);
  const todos = structured.steps.map((s, i) => ({
    id: `t${Number.isFinite(Number(s?.n)) ? Number(s.n) : i + 1}`,
    text: String(s?.action || '').trim(),
    status: 'pending',
  })).filter((t) => t.text);
  return todos.length ? { title, todos } : null;
}

// The PROSE fallback (model narrated a plan instead of calling present_plan): the proposal
// comes from parsePlanTodos, while planFromProse sets a pending plan so the interactive
// card still renders. The two are independent (as inline) — a prose plan can set the
// pending plan even when parsePlanTodos found no todos.
function proseProposal(messages, sessionId, setPendingPlan) {
  const lastAgent = [...messages].reverse().find(
    (m) => m.role === 'agent' && typeof m.content === 'string' && m.content.trim(),
  );
  const planText = lastAgent?.content || '';
  const parsed = parsePlanTodos(planText);
  const proposal = parsed.todos.length ? { title: parsed.title, todos: parsed.todos } : null;
  const prosePlan = planFromProse(planText);
  if (prosePlan) setPendingPlan(sessionId, ensurePlanQuestions(prosePlan));
  return proposal;
}

// pos-s5 — fill the right-rail Tasks DAG the moment a plan is proposed. Best-effort: a
// malformed plan must never block the proposal.
function projectOntoBoard(proposal, sessionId, setCodeTasks) {
  try {
    const planTasks = planTodosToTasks(proposal.todos);
    if (planTasks.length) {
      setCodeTasks({
        runId: `plan:${sessionId}`,
        tasks: planTasks,
        statusByPlanId: {},
        criticalPath: [],
        planDoc: { title: String(proposal.title || 'Plano').trim(), summary: '' },
      });
    }
  } catch { /* board is best-effort; never block the plan */ }
}

/**
 * @param {{
 *   agentResult: { plan?: object } | null,
 *   planPropose: boolean, runOk: boolean, aborted: boolean, sessionId: string,
 *   getMessages: () => any[],                       // () => getRunSnapshot(id).messages
 *   setPendingPlan: (id: string, plan: object) => void,
 *   dispatchPlan: (id: string, action: object) => void,
 *   setCodeTasks: (payload: object) => void,        // useAgentStore setCodeTasks
 *   setTodoOpen: (open: boolean) => void,
 * }} io
 */
export function proposePlanFromOutcome({
  agentResult, planPropose, runOk, aborted, sessionId,
  getMessages, setPendingPlan, dispatchPlan, setCodeTasks, setTodoOpen,
}) {
  const structured = agentResult?.plan;
  const hasStructured = !!(structured && Array.isArray(structured.steps) && structured.steps.length);
  // Only propose on a clean finish that either ran in plan mode or explicitly emitted a
  // structured plan (a spurious present_plan shows the card, not a dangling tool block).
  if (!((planPropose || hasStructured) && runOk && !aborted)) return;
  let proposal = hasStructured ? structuredProposal(structured, sessionId, setPendingPlan) : null;
  if (!proposal && planPropose) proposal = proseProposal(getMessages(), sessionId, setPendingPlan);
  if (!proposal) return;
  dispatchPlan(sessionId, { type: 'propose', title: proposal.title, todos: proposal.todos });
  projectOntoBoard(proposal, sessionId, setCodeTasks);
  setTodoOpen(true);
}
